// client/src/pages/admin/AdminBusinessUsers.js
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  Users, Search, Filter, Download, Eye, Edit, Ban, CheckCircle,
  Building2, TrendingUp, UserX, UserPlus, ChevronDown, ChevronUp
} from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import ConfirmModal from '../../components/common/ConfirmModal';
import { adminAPI } from '../../services/api';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceAreaFilter, setServiceAreaFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [confirmModal, setConfirmModal] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  useEffect(() => {
    fetchData();
  }, [searchTerm, statusFilter, serviceAreaFilter, sortBy, sortOrder]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch businesses
      const params = {
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        serviceArea: serviceAreaFilter !== 'all' ? serviceAreaFilter : undefined,
        sortBy,
        sortOrder
      };

      const [businessesRes, statsRes] = await Promise.all([
        adminAPI.getBusinessUsers(params),
        adminAPI.getBusinessStats()
      ]);

      setBusinesses(businessesRes.data.businesses || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUnblock = async (businessId, currentStatus) => {
    const action = currentStatus ? 'block' : 'unblock';
    setConfirmModal({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Business`,
      message: `Are you sure you want to ${action} this business?`,
      icon: 'warning',
      confirmLabel: action.charAt(0).toUpperCase() + action.slice(1),
      confirmColor: 'yellow',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await adminAPI.blockUnblockBusiness(businessId, { isActive: !currentStatus });
          fetchData();
        } catch (err) {
          console.error('Error updating business status:', err);
          toast.error('Failed to update business status');
        }
      }
    });
  };

  const handleExportCSV = () => {
    const headers = [
      'Business Name',
      'Contact Person',
      'Email',
      'Phone',
      'Service Area',
      'Business Type',
      'Total Orders',
      'Total Spent',
      'Status',
      'Registered Date'
    ];

    const rows = businesses.map(b => [
      b.businessName,
      b.contactPerson,
      b.email,
      b.phone,
      b.serviceArea,
      b.businessType,
      b.totalOrders,
      `€${b.totalSpent.toFixed(2)}`,
      b.isActive ? 'Active' : 'Blocked',
      format(new Date(b.createdAt), 'yyyy-MM-dd')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `businesses-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const getStatusColor = (isActive) => {
    return isActive
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-red-100 text-red-800 border-red-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex justify-center items-center h-64">
          <div className="text-xl text-gray-600">Loading businesses...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="container mx-auto px-4 py-8 pt-16 lg:pt-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Business Users</h1>
          <p className="text-gray-600">Manage and monitor all business accounts</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Businesses</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalBusinesses}</p>
                </div>
                <Building2 className="w-12 h-12 text-blue-500" />
              </div>
            </div>

            <div className="bg-green-50 rounded-lg shadow-md p-6 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-800 mb-1">Active</p>
                  <p className="text-3xl font-bold text-green-900">{stats.activeBusinesses}</p>
                </div>
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg shadow-md p-6 border border-yellow-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-800 mb-1">New This Month</p>
                  <p className="text-3xl font-bold text-yellow-900">{stats.newThisMonth}</p>
                </div>
                <UserPlus className="w-12 h-12 text-yellow-600" />
              </div>
            </div>

            <div className="bg-red-50 rounded-lg shadow-md p-6 border border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-800 mb-1">Blocked</p>
                  <p className="text-3xl font-bold text-red-900">{stats.blockedBusinesses}</p>
                </div>
                <UserX className="w-12 h-12 text-red-600" />
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 mb-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, email, or contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="w-full lg:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>

            {/* Sort */}
            <div className="w-full lg:w-48">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="createdAt">Registration Date</option>
                <option value="businessName">Business Name</option>
                <option value="totalOrders">Total Orders</option>
                <option value="totalSpent">Total Spent</option>
                <option value="lastOrderDate">Last Order</option>
              </select>
            </div>

            {/* Sort Order */}
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
            >
              {sortOrder === 'asc' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            </button>

            {/* Export */}
            <button
              onClick={handleExportCSV}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <Download className="w-5 h-5" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 text-sm text-gray-600">
          Showing {businesses.length} {businesses.length === 1 ? 'business' : 'businesses'}
        </div>

        {/* Businesses Table */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {businesses.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No businesses found</h3>
            <p className="text-gray-500">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Service Area
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Orders / Spent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registered
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {businesses.map((business) => (
                    <tr key={business.userId} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{business.businessName}</div>
                          <div className="text-sm text-gray-500">{business.businessType}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm text-gray-900">{business.contactPerson}</div>
                          <div className="text-sm text-gray-500">{business.email}</div>
                          <div className="text-sm text-gray-500">{business.phone}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{business.serviceArea}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {business.totalOrders} orders
                          </div>
                          <div className="text-sm text-gray-500">
                            €{business.totalSpent.toFixed(2)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(business.isActive)}`}>
                          {business.isActive ? 'Active' : 'Blocked'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {format(new Date(business.createdAt), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => navigate(`/admin/business/${business.businessId}`)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleBlockUnblock(business.businessId, business.isActive)}
                            className={`p-2 rounded-lg transition-colors ${
                              business.isActive
                                ? 'text-red-600 hover:bg-red-50'
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            title={business.isActive ? 'Block Business' : 'Unblock Business'}
                          >
                            {business.isActive ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-gray-200">
              {businesses.map((business) => (
                <div key={business.userId} className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{business.businessName}</h3>
                      <p className="text-sm text-gray-500">{business.businessType}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(business.isActive)}`}>
                      {business.isActive ? 'Active' : 'Blocked'}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm mb-3">
                    <div>
                      <span className="text-gray-500">Contact:</span>{' '}
                      <span className="text-gray-900">{business.contactPerson}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Email:</span>{' '}
                      <span className="text-gray-900">{business.email}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Service Area:</span>{' '}
                      <span className="text-gray-900">{business.serviceArea}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Orders:</span>{' '}
                      <span className="text-gray-900">{business.totalOrders} (€{business.totalSpent.toFixed(2)})</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Registered:</span>{' '}
                      <span className="text-gray-900">{format(new Date(business.createdAt), 'MMM dd, yyyy')}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/admin/business/${business.businessId}`)}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View Details
                    </button>
                    <button
                      onClick={() => handleBlockUnblock(business.businessId, business.isActive)}
                      className={`px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                        business.isActive
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {business.isActive ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      {business.isActive ? 'Block' : 'Unblock'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default AdminUsers;